import React, { useEffect, useState } from "react";
import { BASE_URL, mixContentQuestionsApi } from "../../api/api";
import api from "../../utils/api";
import { motion, AnimatePresence } from "framer-motion";
import {
    Search,
    ArrowUp,
    ArrowDown,
    ExternalLink,
    FileText,
    Loader2,
    AlertCircle,
} from "lucide-react";

const MixContent = () => {
    const [questions, setQuestions] = useState([]);
    const [filteredQuestions, setFilteredQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [sortOrder, setSortOrder] = useState("desc"); // Default to newest first

    // Get student ID from session storage
    const studentSession = sessionStorage.getItem("studentSession");
    const id = studentSession ? JSON.parse(studentSession).id : null;

    // Fetch data from API
    useEffect(() => {
        const fetchQuestions = async () => {
            if (!id) {
                setError("No student session found.");
                setLoading(false);
                return;
            }
            try {
                const response = await api.get(
                    `${mixContentQuestionsApi}/${id}`,
                );
                const data = response.data?.data || [];

                // Pre-sort by descending date
                const sortedData = [...data].sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at),
                );

                setQuestions(sortedData);
                setFilteredQuestions(sortedData);
            } catch (err) {
                // setError("Failed to fetch data. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchQuestions();
    }, [id]);

    // Decode HTML safely for the question field search
    const decodeHTML = (html) => {
        if (!html) return "";
        const el = document.createElement("div");
        el.innerHTML = html;
        return el.textContent || el.innerText || "";
    };

    // Handle search input
    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setSearchTerm(term);
        if (term === "") {
            setFilteredQuestions(questions);
        } else {
            const filtered = questions.filter((q) =>
                decodeHTML(q.question)?.toLowerCase().includes(term),
            );
            setFilteredQuestions(filtered);
        }
    };

    // Handle sort button click (sort by created_at)
    const handleSort = () => {
        const newOrder = sortOrder === "asc" ? "desc" : "asc";
        const sorted = [...filteredQuestions].sort((a, b) => {
            const dateA = new Date(a.created_at);
            const dateB = new Date(b.created_at);
            return newOrder === "asc" ? dateA - dateB : dateB - dateA;
        });
        setFilteredQuestions(sorted);
        setSortOrder(newOrder);
    };

    // Helper to safely format answer URLs
    const formatUrl = (url) => {
        if (!url) return "#";
        return url.startsWith("http") ? url : `https://${url}`;
    };

    // Framer Motion Variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.05 },
        },
    };

    const rowVariants = {
        hidden: { opacity: 0, y: 10 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.3 },
        },
    };

    return (
        <div className="w-full mx-auto p-4 sm:p-6 lg:p-8">
            {/* Standardized Editorial Header */}
            <div className="mb-8">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-2">
                        Mix Content{" "}
                        <span className="text-yellow-500">Report.</span>
                    </h2>
                    <p className="text-gray-500 text-sm md:text-base font-medium">
                        Review your mixed content submissions and mentor
                        evaluations.
                    </p>
                </motion.div>
            </div>

            {/* Controls Section */}
            <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="flex flex-col sm:flex-row gap-4 justify-between items-center mb-6"
            >
                <div className="relative w-full sm:max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search size={18} className="text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Search by question..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 bg-white text-neutral-900 font-medium placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-400/50 focus:border-neutral-900 transition-all duration-300 shadow-sm"
                    />
                </div>

                <button
                    onClick={handleSort}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-white border border-gray-200 text-neutral-900 rounded-lg font-bold hover:bg-gray-50 hover:border-neutral-900 transition-all duration-300 shadow-sm"
                >
                    Sort by Date
                    {sortOrder === "asc" ? (
                        <ArrowUp size={16} className="text-red-700" />
                    ) : (
                        <ArrowDown size={16} className="text-red-700" />
                    )}
                </button>
            </motion.div>

            {/* Table Card Container */}
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-xs font-bold text-neutral-900 uppercase tracking-wider whitespace-nowrap">
                                    S.No
                                </th>
                                <th className="px-6 py-4 text-xs font-bold text-neutral-900 uppercase tracking-wider min-w-[300px]">
                                    Question
                                </th>
                                <th className="px-6 py-4 text-xs font-bold text-neutral-900 uppercase tracking-wider whitespace-nowrap text-center">
                                    Answer
                                </th>
                                <th className="px-6 py-4 text-xs font-bold text-neutral-900 uppercase tracking-wider min-w-[200px]">
                                    Remark
                                </th>
                                <th className="px-6 py-4 text-xs font-bold text-neutral-900 uppercase tracking-wider whitespace-nowrap">
                                    Submitted On
                                </th>
                            </tr>
                        </thead>

                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="px-6 py-12 text-center"
                                    >
                                        <div className="flex flex-col items-center justify-center text-gray-500">
                                            <Loader2
                                                size={32}
                                                className="animate-spin mb-4 text-yellow-500"
                                            />
                                            <p className="font-medium">
                                                Loading your reports...
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="px-6 py-12 text-center"
                                    >
                                        <div className="flex flex-col items-center justify-center text-red-600">
                                            <AlertCircle
                                                size={32}
                                                className="mb-4"
                                            />
                                            <p className="font-bold">{error}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : filteredQuestions.length > 0 ? (
                                <AnimatePresence>
                                    {filteredQuestions.map((item, index) => (
                                        <motion.tr
                                            key={item.id || index}
                                            variants={rowVariants}
                                            initial="hidden"
                                            animate="visible"
                                            className="hover:bg-gray-50/50 transition-colors duration-200 group"
                                        >
                                            <td className="px-6 py-5 text-sm font-bold text-gray-400 whitespace-nowrap">
                                                {String(index + 1).padStart(
                                                    2,
                                                    "0",
                                                )}
                                            </td>
                                            <td className="px-6 py-5 text-sm font-medium text-neutral-900">
                                                <div
                                                    className="line-clamp-3 group-hover:line-clamp-none transition-all duration-300"
                                                    dangerouslySetInnerHTML={{
                                                        __html: item.question,
                                                    }}
                                                />
                                            </td>
                                            <td className="px-6 py-5 whitespace-nowrap text-center">
                                                <a
                                                    href={formatUrl(
                                                        item.answer,
                                                    )}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-neutral-900 text-white text-xs font-bold rounded-md hover:bg-yellow-400 hover:text-neutral-900 transition-colors duration-300 shadow-sm"
                                                >
                                                    View{" "}
                                                    <ExternalLink size={12} />
                                                </a>
                                            </td>
                                            <td className="px-6 py-5 text-sm text-gray-600 font-medium">
                                                {item.remark ? (
                                                    <span className="bg-gray-100 px-3 py-1.5 rounded-md inline-block">
                                                        {item.remark}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-400 italic">
                                                        Pending Evaluation
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-5 text-sm font-bold text-neutral-900 whitespace-nowrap">
                                                {item.created_at
                                                    ? new Date(
                                                          item.created_at,
                                                      ).toLocaleDateString(
                                                          "en-GB",
                                                          {
                                                              day: "numeric",
                                                              month: "short",
                                                              year: "numeric",
                                                          },
                                                      )
                                                    : "-"}
                                            </td>
                                        </motion.tr>
                                    ))}
                                </AnimatePresence>
                            ) : (
                                <tr>
                                    <td
                                        colSpan="5"
                                        className="px-6 py-16 text-center"
                                    >
                                        <div className="flex flex-col items-center justify-center text-gray-400">
                                            <FileText
                                                size={48}
                                                className="mb-4 opacity-50"
                                                strokeWidth={1.5}
                                            />
                                            <p className="text-lg font-bold text-neutral-900 mb-1">
                                                No reports found.
                                            </p>
                                            <p className="text-sm font-medium">
                                                Try adjusting your search
                                                criteria or submit a new answer.
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </motion.div>
        </div>
    );
};

export default MixContent;
